import classNames from "classnames";

import {
  CharacterCircle,
  CheckActiveIcon,
  ExclamationMarkCircle,
  QuestionMarkCircle,
  UnclockCircle,
  InfoIcon,
  ModalLoadingIcon,
} from "../AscIcons";
import AscModal from "../AscModal/AscModal";
import {
  AscModalClassicIcons,
  AscModalClassicPropsI,
} from "./AscModalClassic.type";
import styles from "./AscModalClassic.module.scss";
import BadgeModalBackground from "../../assets/illustrations/badge-modal-background.svg";
import ExclamationMarkBackground from "../../assets/illustrations/exclamation-mark-modal-background.svg";
import ExpertModalBackground from "../../assets/illustrations/expert-modal-background.svg";
import QuestionMarkModalBackground from "../../assets/illustrations/question-mark-modal-background.svg";
import SuccessModalBackground from "../../assets/illustrations/success-modal-background.svg";
import UnlockModalBackground from "../../assets/illustrations/unlock-modal-background.svg";
import {
  ModalIconDecor,
  SmallPinkTicket,
  FailureModalBackground,
} from "../AscIllustrations";
import AscButton from "../AscButton/AscButton";

function AscModalClassic({
  visible,
  setVisible,
  onClose,
  type,
  icon,
  title,
  description,
  cancelConfirmClassName,
  confirmButtonText = "Confirm",
  cancelButtonText = "Cancel",
  confirmButtonAction,
  cancelButtonAction,
  withBackground = false,
  confirmLoading = null,
}: AscModalClassicPropsI) {
  function closeModal() {
    if (onClose) {
      onClose();
      return;
    }
    setVisible?.(false);
  }

  const getIcon = (icon: AscModalClassicIcons) => {
    switch (icon) {
      case "exclamation-mark":
        return (
          <>
            <ExclamationMarkCircle
              className={styles.icon}
              data-testid={"test-icon"}
            />
            <ModalIconDecor className={styles.iconDecor} />
          </>
        );
      case "question-mark":
        return (
          <>
            <QuestionMarkCircle className={styles.icon} />
            <ModalIconDecor className={styles.iconDecor} />
          </>
        );
      case "unclock":
        return (
          <>
            <UnclockCircle className={styles.icon} />
            <ModalIconDecor className={styles.iconDecor} />
          </>
        );
      case "expert":
        return <CharacterCircle className={styles.icon} />;
      case "success":
        return <CheckActiveIcon className={styles.icon} />;
      case "question":
        return <span className={styles.questionIcon}>?</span>;
      case "info":
        return (
          <span className={styles.infoIconWrapper}>
            <InfoIcon className={styles.infoIcon} />
          </span>
        );
      case "badge":
        return <SmallPinkTicket className={styles.icon} />;
      case "loading":
        return <ModalLoadingIcon className={styles.loadingIcon} />;
      case "failure":
        return <FailureModalBackground className={styles.icon} />;
      default:
        return null;
    }
  };

  const getBackground = (icon: AscModalClassicIcons) => {
    switch (icon) {
      case "exclamation-mark":
        return (
          <img
            className={styles.background}
            src={ExclamationMarkBackground}
            alt={"background"}
          />
        );
      case "failure":
        return (
          <img
            className={styles.background}
            src={SuccessModalBackground}
            alt={"background"}
          />
        );
      case "question-mark":
        return (
          <img
            className={styles.background}
            src={QuestionMarkModalBackground}
            alt={"background"}
          />
        );
      case "unclock":
        return (
          <img
            className={styles.background}
            src={UnlockModalBackground}
            alt={"background"}
          />
        );
      case "expert":
        return (
          <img
            className={styles.background}
            src={ExpertModalBackground}
            alt={"background"}
          />
        );
      case "info":
        return (
          <img
            className={styles.background}
            src={SuccessModalBackground}
            alt={"background"}
          />
        );
      case "question":
        return (
          <img
            className={styles.background}
            src={SuccessModalBackground}
            alt={"background"}
          />
        );
      case "success":
        return (
          <img
            className={styles.background}
            src={SuccessModalBackground}
            alt={"background"}
          />
        );
      case "badge":
        return (
          <img
            className={styles.background}
            src={BadgeModalBackground}
            alt={"background"}
          />
        );
      case "loading":
        return (
          <img
            className={styles.background}
            src={SuccessModalBackground}
            alt={"background"}
          />
        );
      default:
        return null;
    }
  };

  return (
    <AscModal
      visible={visible}
      setVisible={setVisible}
      modalClassName={styles.modalClassName}
      backdropClassName={styles.backdropClassName}
      withBackground={withBackground}
      onClose={closeModal}
    >
      <div className={styles.element}>
        {getBackground(icon)}
        <div className={styles.inner}>
          <div className={styles.top}>
            <div className={styles.iconWrapper}>{getIcon(icon)}</div>
            <div className={styles.title}>{title}</div>
            {description && (
              <div className={styles.description}>{description}</div>
            )}
          </div>
          <div
            className={classNames(
              styles.bottom,
              styles[type],
              cancelConfirmClassName
            )}
          >
            {(type === "cancel-and-confirm" || type === "confirm") && (
              <>
                {type === "cancel-and-confirm" && (
                  <AscButton
                    className={styles.button}
                    variant={"secondary"}
                    onClick={() => {
                      closeModal();
                      cancelButtonAction && cancelButtonAction();
                    }}
                    tabIndex={0}
                  >
                    {cancelButtonText}
                  </AscButton>
                )}
                {confirmButtonAction && (
                  <AscButton
                    className={styles.button}
                    onClick={() => {
                      confirmButtonAction();
                      confirmLoading === null && closeModal();
                    }}
                    loading={!!confirmLoading}
                    tabIndex={0}
                  >
                    {confirmButtonText}
                  </AscButton>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </AscModal>
  );
}

export default AscModalClassic;
